import { getAccessToken } from "./supabase"
import Search from "../types/search"

//
// URL
//
export interface BackapiConfig {
  backapiUrl?: string
  domain?: string
  environment?: string
  platform?: string
}

let _backapiUrl = ""

/** Legacy mutable alias — prefer `getBackapiUrl()` after `initBackapiUrl`. */
export let BACKAPI_URL = ""

export function buildBackapiUrl(config: BackapiConfig): string {
  const { backapiUrl, domain, environment, platform } = config
  if (backapiUrl) return backapiUrl

  const backPrefix =
    process.env.NEXT_PUBLIC_BACK_PREFIX ??
    process.env.EXPO_PUBLIC_BACK_PREFIX ??
    "api"
  const subdomainSuffix =
    process.env.NEXT_PUBLIC_SUBDOMAIN_SUFFIX ??
    process.env.EXPO_PUBLIC_SUBDOMAIN_SUFFIX ??
    ""
  const suffix = subdomainSuffix ? `-${subdomainSuffix}` : ""

  if (platform === "local") return `http://${backPrefix}.localhost:83`
  if (environment === "production") return `https://${backPrefix}${suffix}.${domain}`
  return `https://${backPrefix}-${environment}${suffix}.${domain}`
}

export function getBackapiUrl(): string {
  return _backapiUrl || BACKAPI_URL
}

export function initBackapiUrl(url: string): void {
  _backapiUrl = url
  BACKAPI_URL = url
}

//
// HELPERS
//
async function messageFromFastapiErrorResponse(
  response: Response,
  fallback: string
): Promise<string> {
  let detail = fallback
  try {
    const errBody = (await response.json()) as { detail?: string | string[] }
    if (typeof errBody.detail === "string") detail = errBody.detail
    else if (
      Array.isArray(errBody.detail) &&
      errBody.detail.length > 0 &&
      typeof errBody.detail[0] === "object" &&
      errBody.detail[0] !== null &&
      "msg" in errBody.detail[0]
    ) {
      detail = String((errBody.detail[0] as { msg: string }).msg)
    }
  } catch {
    /* keep fallback */
  }
  return detail
}

function buildApiUrl(path: string, query: URLSearchParams): string {
  const pathNormalized = path.replace(/^\//, "").replace(/\/$/, "")
  const queryString = query.toString()
  const base = getBackapiUrl().replace(/\/$/, "")
  let url = `${base}/${pathNormalized}`
  if (queryString) {
    url += pathNormalized.includes("?") ? `&${queryString}` : `?${queryString}`
  }
  return url
}

export async function getEntities<T>(
  path: string,
  search: Search,
  select?: string | string[]
): Promise<T[]> {
  const token = await getAccessToken()
  if (!token) throw new Error("No active session")

  const { ascending, filter, key, limit, offset, order, value } = search
  const query = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  })

  if (value && value.trim()) query.set("value", value.trim())
  if (filter) {
    for (const [k, v] of Object.entries(filter)) {
      if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") {
        query.set(k, String(v))
      }
      // Nested objects = join filters, only supported in Supabase getEntities
    }
  }

  if (order) {
    query.set("order", order)
    if (ascending) query.set("ascending", String(ascending))
  }

  if (key) query.set("key", Array.isArray(key) ? key.join(",") : key)

  if (select) query.set("select", Array.isArray(select) ? select.join(",") : select)

  const url = buildApiUrl(path, query)
  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  })

  if (!response.ok) {
    const detail = await messageFromFastapiErrorResponse(
      response,
      response.statusText || "Erreur lors du chargement"
    )
    const err = new Error(detail) as Error & { status: number }
    err.status = response.status
    throw err
  }

  return response.json()
}

export async function getEntity<T>(
  path: string,
  id: string,
  select?: string | string[]
): Promise<T> {
  const token = await getAccessToken()
  if (!token) throw new Error("No active session")

  const query = new URLSearchParams()
  if (select) query.set("select", Array.isArray(select) ? select.join(",") : select)

  const url = buildApiUrl(`${path}/${id}`, query)
  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  })

  if (!response.ok) {
    const err = new Error(
      response.statusText || "Erreur lors du chargement"
    ) as Error & { status: number }
    err.status = response.status
    throw err
  }

  return response.json()
}

export async function postEntity<T>(
  path: string,
  body: T,
  select?: string | string[]
): Promise<T> {
  const token = await getAccessToken()
  if (!token) throw new Error("No active session")

  const query = new URLSearchParams()
  if (select) query.set("select", Array.isArray(select) ? select.join(",") : select)

  const url = buildApiUrl(path, query)
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  })

  if (!response.ok) {
    const detail = await messageFromFastapiErrorResponse(
      response,
      response.statusText || "Erreur lors de l’envoi"
    )
    const err = new Error(detail) as Error & { status: number }
    err.status = response.status
    throw err
  }

  return response.json()
}


export async function patchEntity<T>(
  path: string,
  id: string,
  body: T,
  select?: string | string[]
): Promise<T> {
  const token = await getAccessToken()
  if (!token) throw new Error("No active session")

  const query = new URLSearchParams()
  if (select) query.set("select", Array.isArray(select) ? select.join(",") : select)
    
  const url = buildApiUrl(`${path}/${id}`, query)
  const response = await fetch(url, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  })

  if (!response.ok) {
    const err = new Error(
      response.statusText || "Erreur lors du chargement"
    ) as Error & { status: number }
    err.status = response.status
    throw err }

  return response.json()
}

export async function putEntity<T>(
  path: string,
  body: T,
  select?: string | string[]
): Promise<T> {
  const token = await getAccessToken()
  if (!token) throw new Error("No active session")

  const query = new URLSearchParams()
  if (select) query.set("select", Array.isArray(select) ? select.join(",") : select)

  const url = buildApiUrl(path, query)
  const response = await fetch(url, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  })

  if (!response.ok) {
    const err = new Error(
      response.statusText || "Erreur lors du chargement"
    ) as Error & { status: number }
    err.status = response.status
    throw err
  }

  return response.json()
}

export async function postFormData<T>(
  path: string,
  form: FormData
): Promise<T> {
  const token = await getAccessToken()
  if (!token) throw new Error("No active session")

  const url = buildApiUrl(path, new URLSearchParams())
  const response = await fetch(url, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  })

  if (!response.ok) {
    const err = new Error(
      response.statusText || "Erreur lors du chargement"
    ) as Error & { status: number }
    err.status = response.status
    throw err
  }

  return response.json()
}

export async function deleteEntity<T>(
  path: string,
  id: string
): Promise<T> {
  const token = await getAccessToken()
  if (!token) throw new Error("No active session")

  const url = buildApiUrl(`${path}/${id}`, new URLSearchParams())
  const response = await fetch(url, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  })  

  if (!response.ok) {
    const err = new Error(
      response.statusText || "Erreur lors du chargement"
    ) as Error & { status: number }
    err.status = response.status
    throw err
  }

  return response.json()
}
